import { useTranslation } from 'react-i18next';
import { useLiveStreamLatency } from '@/hooks/useLiveStream';

/**
 * Live-stream ingest latency badge. Polls
 * `GET /sessions/:id/live-stream/latency` (latency-rx, spawned per live stream) and shows
 * `latest_ms` with a green/yellow/red tier. Renders nothing until the first sample arrives
 * (e.g. while latency-rx is connecting — the API answers 202 with no data); solid colors read on both
 * the dark team-tile overlay and the light session-detail card.
 *
 * Tier: < 500 ms green, < 1000 ms yellow, >= 1000 ms red.
 */
export function LatencyBadge({
  sessionId,
  variant = 'pill',
}: {
  sessionId: string;
  /**
   * "pill" is the standalone solid badge. "strap" is bare tinted text for the
   * Team Live tile strap, where a second pill next to the elapsed time read as
   * clutter (issue #738).
   */
  variant?: 'pill' | 'strap';
}) {
  const { t } = useTranslation('pages');
  const { data } = useLiveStreamLatency(sessionId);
  if (!data) return null;
  const ms = Math.round(data.latest_ms);
  const tier =
    variant === 'strap'
      ? ms < 500
        ? 'text-green-400'
        : ms < 1000
          ? 'text-yellow-400'
          : 'text-red-400'
      : ms < 500
        ? 'bg-green-500 text-white'
        : ms < 1000
          ? 'bg-yellow-500 text-black'
          : 'bg-red-500 text-white';
  return (
    <span
      title={t('sessions.detail.liveStream.latency')}
      className={
        variant === 'strap'
          ? `whitespace-nowrap font-semibold ${tier}`
          : `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${tier}`
      }
    >
      {ms}ms
    </span>
  );
}
